import { Menu } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCartOutlined,
  FileTextOutlined,
  LogoutOutlined,
  DollarCircleOutlined,
  AppstoreOutlined,
  CarOutlined,
  SettingOutlined,
  HomeOutlined,
  PlusOutlined,
  UserOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import './sidebar.css';
import axios from 'axios';
import config from '../../config';
import { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '../../redux/userRedux';
import usePermissions from '../../hooks/usePermissions';

const { SubMenu, Item } = Menu;

// Navigation réorganisée par PARCOURS MÉTIER (Phase 0). La structure est définie
// en code (7 sections), mais la VISIBILITÉ ne change rien à l'existant : on
// n'affiche que les routes que l'utilisateur a déjà (ses menus en base +, pour
// l'admin, ses raccourcis). Le filtrage fin par rôle viendra avec la refonte
// des permissions. Chaque route est rangée dans sa section ; les sections sans
// aucun élément autorisé disparaissent.
const SECTIONS = [
  { key: 'sec-accueil', title: 'Accueil', icon: HomeOutlined, items: [
    { label: 'Accueil', route: '/' },
    { label: 'En direct', route: '/mobile' },
    // Troisième surface, à l'essai : l'argent d'abord, les alertes après.
    // Accueil et « En direct » restent inchangés — on compare, on ne remplace pas.
    { label: 'Pilotage', route: '/pilotage' },
  ] },
  { key: 'sec-commerce', title: 'Commerce', icon: ShoppingCartOutlined, items: [
    // « Nouvelle commande » est volontairement sortie en raccourci proéminent (bouton or en haut),
    // donc on ne la duplique PAS ici (sinon clé /commandeForm en double).
    { label: 'Commandes', sub: [
      { label: 'Liste des commandes', route: '/listeCommande' },
      { label: 'Commandes dormantes', route: '/commandesDormantes' },
    ] },
    { label: 'Clientes', route: '/clients' },
    { label: 'Ventes et encaissements', sub: [
      { label: 'Ventes confirmées', route: '/ventes' },
      { label: 'Centre des créances', route: '/creances' },
      { label: 'Paiement', route: '/paiement' },
      { label: 'Vente à crédit', route: '/dette' },
      { label: 'Mouvements', route: '/mouvement' },
    ] },
    { label: 'Promotions', route: '/promotions' },
    { label: 'Centre de régularisation', route: '/regularisation' },
    { label: 'Réservations', route: '/reservations' },
    { label: 'Options / pré-commandes', route: '/precommandes' },
  ] },
  // ③ Repositionnement : Livraison (opérationnel, quotidien) remonte AVANT
  // Catalogue (plus statique). Aucune route touchée → visibilité inchangée.
  { key: 'sec-livraison', title: 'Livraison et après-vente', icon: CarOutlined, items: [
    { label: 'Livraisons', sub: [
      { label: 'Détail livraison', route: '/livraison_detail' },
      { label: 'Supervision des livraisons', route: '/supervision-livraisons' },
    ] },
    { label: 'Centre des échanges', route: '/centreEchanges' },
    { label: 'Retours et échanges', route: '/echangeForm' },
    { label: 'Retours clients', route: '/retour' },
    { label: 'Livreurs', route: '/livreur' },
  ] },
  { key: 'sec-catalogue', title: 'Catalogue et stock', icon: AppstoreOutlined, items: [
    // L'ASSISTANT EN TÊTE DE SECTION (10/08/2026). Il était livré depuis la
    // veille et n'existait NULLE PART dans le menu : on ne l'atteignait que par
    // un bouton de la page « Catalogue visuel ». Un écran qu'on ne trouve pas
    // n'est pas livré. Il passe donc en premier, là où l'on cherche à créer.
    { label: '✨ Nouveau modèle (avec son arrivage)', route: '/catalogue/nouveau' },
    { label: 'Catalogue visuel', route: '/varianteProduit' },
    { label: 'Catalogue de partage', route: '/partage-catalogue' },
    { label: 'Photos à partager', route: '/photos-partage' },
    // Qualifier les modèles vit ICI, pas dans Administration : c'est un geste
    // de catalogue. Enfouie sous « Paramètres catalogue », l'entrée était
    // introuvable (constat de la propriétaire, 20/08/2026).
    { label: 'Qualifier les modèles', route: '/qualification-criteres' },
    { label: 'Produits et gestion', sub: [
      { label: 'Liste des produits', route: '/products' },
      // L'ANCIEN FORMULAIRE EST RETIRÉ DU MENU (10/08/2026, décision de la
      // propriétaire : « le nouveau modèle devrait aussi remplacer le menu
      // création de produit »).
      //
      // Il portait quatre pièges qu'aucun écran ne savait réparer — dont l'état
      // « Passif », qui rendait un modèle invisible au catalogue à vie. Aucun
      // n'avait encore tiré (mesuré : 0 produit concerné), et on ferme avant.
      //
      // La ROUTE serveur reste en place quelques semaines : le retour arrière
      // tient en une ligne si un usage oublié apparaît.
    ] },
    { label: 'Réceptions', sub: [
      { label: 'Entrée de stock', route: '/entreeStock' },
      { label: 'Liste des réceptions', route: '/reception' },
      { label: "Coûts d'achat", route: '/couts-achat' },
    ] },
    { label: 'Stock et réassort', sub: [
      { label: 'Journal de stock', route: '/journal_stock' },
      { label: 'Journal des transactions', route: '/journal_transactions' },
      { label: 'Réconciliation des stocks', route: '/reconciliation_stocks' },
      { label: 'Inventaires', route: '/inventaires' },
      { label: 'Correction de stock', route: '/mobile?section=correction-stock' },
      { label: 'Liste des variantes', route: '/listeVariante' },
    ] },
  ] },
  // Rapports regroupés PAR NATURE MÉTIER (demande proprio 20/07) : la question
  // que chaque rapport répond, pas la technique. Les 2 synthèses restent en
  // accès direct ; « Ventes manquées & zones » (hybride) apparaît dans DEUX
  // groupes — même route, deux chemins (clé d'item préfixée par le groupe).
  { key: 'sec-pilotage', title: 'Pilotage', icon: FileTextOutlined, items: [
    { label: 'Performance et réassort', route: '/mobile?catalogue=1' },
    { label: "Rapports — vue d'ensemble", route: '/rapports' },
    { label: 'Décisions recommandées', route: '/rapports/decisions' },
    { label: '📈 Évolution des ventes', route: '/rapports/evolution' },
    { label: '💰 Argent', sub: [
      { label: 'Ventes', route: '/rapports/ventes' },
      { label: 'Ventes par vendeur', route: '/rapports/vendeurs' },
      { label: 'Revenus', route: '/rapports/revenus' },
      { label: 'Dépenses et résultat', route: '/rapports/depenses' },
      { label: 'CA par ancienneté du stock', route: '/rapports/anciennete' },
    ] },
    { label: '👩 Clientes', sub: [
      { label: 'Qui relancer', route: '/rapports/relance' },
      { label: 'Fidélisation & rétention', route: '/rapports/fidelisation' },
      { label: 'Clientes', route: '/rapports/clients' },
      { label: 'Ventes manquées & zones', route: '/rapports/pertes-zones' },
    ] },
    { label: '👠 Assortiment', sub: [
      { label: 'Analyse pointures', route: '/rapports/pointures' },
      { label: 'Couleurs, marques & talon', route: '/rapports/dimensions' },
      { label: 'Essais & retours par modèle', route: '/rapports/retours' },
      { label: 'Performance produits', route: '/rapports/performance' },
    ] },
    { label: '📦 Stock & réassort', sub: [
      { label: 'Ruptures intéressantes', route: '/rapports/ruptures' },
      { label: 'Ventes manquées & zones', route: '/rapports/pertes-zones' },
      { label: 'Rotation stock', route: '/rapports/rotation' },
      { label: 'Produits dormants', route: '/rapports/dormants' },
      { label: 'Approvisionnement', route: '/rapports/approvisionnement' },
    ] },
    { label: '🔍 Contrôle', sub: [
      { label: 'Analyse des modifications', route: '/rapports/modifications' },
    ] },
  ] },
  { key: 'sec-finance', title: 'Finance', icon: DollarCircleOutlined, items: [
    { label: 'Dépenses', sub: [
      { label: 'Liste des dépenses', route: '/depensesAll' },
      { label: 'Catégories de dépenses', route: '/catDepenses' },
      { label: 'Ma feuille du jour', route: '/ma-feuille-depense' },
      { label: 'Feuilles à valider', route: '/validation-depenses' },
      { label: 'Coûts de trajet', route: '/analyse-depenses' },
    ] },
    { label: 'Caisse', route: '/caisse' },
  ] },
  { key: 'sec-admin', title: 'Administration', icon: SettingOutlined, items: [
    { label: 'Équipe et utilisateurs', route: '/utilisateurs' },
    { label: 'Paramètres catalogue', sub: [
      { label: 'Catégories', route: '/categories' },
      { label: 'Emplacements', route: '/emplacement' },
      { label: 'Matières', route: '/matiere' },
      { label: 'Marques', route: '/marque' },
      { label: 'Couleurs', route: '/couleur' },
      { label: 'Hauteur de talon', route: '/hauteur-talon' },
      // Les CRITÈRES eux-mêmes (24/08/2026). « Qualifier les modèles » sert à
      // RÉPONDRE aux questions ; cet écran-ci sert à les POSER. Il vit donc
      // parmi les référentiels — Couleurs, Matières — et non à côté de la
      // qualification, qui est un geste de catalogue quotidien.
      { label: 'Critères de qualification', route: '/parametres-criteres' },
      { label: 'Types de mouvement', route: '/typeMouvement' },
    ] },
    { label: 'Accès et permissions', sub: [
      { label: 'Matrice des permissions', route: '/permissions-matrice' },
      { label: 'Utilisateurs (liste)', route: '/permission' },
    ] },
    { label: 'Journal des modifications', route: '/journal-modifications' },
    { label: 'Journal des suppressions', route: '/journal-suppressions' },
    { label: 'Notifications WhatsApp', route: '/notifications-whatsapp' },
    { label: "Sentinelle d'intégrité", route: '/sentinelle' },
    { label: 'Fusion de doublons', route: '/fusion-doublons' },
    { label: 'Paramètres de la boutique', route: '/parametres-boutique' },
    { label: 'Paramètres (livraison V2)', route: '/mobile?section=parametres' },
  ] },
];

const ADMIN_SHORTCUTS = ['/pilotage', '/mobile', '/mobile?catalogue=1', '/mobile?section=correction-stock', '/mobile?section=parametres', '/journal_transactions', '/reconciliation_stocks', '/supervision-livraisons', '/reservations', '/precommandes', '/inventaires', '/partage-catalogue', '/parametres-boutique', '/permissions-matrice', '/rapports/ventes', '/rapports/vendeurs', '/rapports/clients', '/rapports/revenus', '/rapports/approvisionnement', '/rapports/depenses', '/rapports/performance', '/rapports/ruptures', '/rapports/rotation', '/rapports/dormants', '/rapports/pointures', '/rapports/anciennete', '/rapports/relance', '/rapports/fidelisation', '/rapports/dimensions', '/rapports/pertes-zones', '/rapports/retours', '/rapports/evolution', '/rapports/decisions', '/rapports', '/couts-achat', '/catalogue/nouveau', '/journal-modifications', '/journal-suppressions', '/notifications-whatsapp', '/rapports/modifications', '/sentinelle', '/hauteur-talon', '/qualification-criteres', '/parametres-criteres'];

const rootKeys = SECTIONS.map((s) => s.key);

const Options = ({ data }) => {
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState('');
  const userRole = useSelector((state) => state.user?.currentUser?.role);
  const isSidebarOpen = useSelector((state) => state.user?.isSidebarOpen);
  const isAdmin = userRole === 'admin';
  const { can } = usePermissions();

  // Ensemble des routes RÉELLEMENT accessibles à l'utilisateur (accès inchangé) :
  // ses menus en base + (admin) ses raccourcis + l'accueil et son profil.
  const visible = new Set(['/', '/profile']);
  (data || []).forEach((m) => (m.subMenus || []).forEach((s) => s.submenu_url && visible.add(s.submenu_url)));
  if (isAdmin) ADMIN_SHORTCUTS.forEach((r) => visible.add(r));
  // Actions sans entrée de menu dédiée : rattachées à leur liste.
  if (visible.has('/listeCommande')) visible.add('/commandeForm');
  // `/productForm` n'est plus proposé nulle part (voir SECTIONS) : on cesse de
  // l'ouvrir. La route existe encore côté serveur, mais aucun écran n'y mène.
  // if (visible.has('/products')) visible.add('/productForm');
  // L'assistant « Nouveau modèle » remplace l'ancien formulaire de création.
  //
  // Il était montré à qui voit « Coûts d'achat » — parce qu'il crée aussi un
  // arrivage. Mais côté serveur, seul l'ajout d'une FACTURE relève des rôles
  // « coûts » : la fiche et son stock sont ouverts à `admin, secretaire`, comme
  // l'ancien formulaire. La règle d'ici était donc plus étroite que le serveur,
  // et une secrétaire n'avait plus AUCUN moyen de créer un modèle (signalé en
  // production le 12/08/2026 : « je n'ai pas les accès »).
  //
  // On s'aligne sur le rôle, puisque c'est le rôle que le serveur regarde :
  // au-delà, l'entrée mènerait à un 403 — en deçà, elle cache un droit réel.
  if (userRole === 'admin' || userRole === 'secretaire') visible.add('/catalogue/nouveau');
  // Centre des créances : accessible à qui gère déjà les dettes/paiements.
  if (visible.has('/dette') || visible.has('/paiement')) visible.add('/creances');
  // Modules gouvernés STRICTEMENT par la permission fine (Phase 2c) : une
  // entrée submenus en base ne suffit pas — la page refuserait sans le droit.
  // L'entrée apparaît donc exactement pour qui peut agir (rôle ou dérogation).
  visible.delete('/photos-partage');
  visible.delete('/promotions');
  visible.delete('/regularisation');
  visible.delete('/ma-feuille-depense');
  visible.delete('/validation-depenses');
  visible.delete('/analyse-depenses');
  if (can('photos_partage.gerer')) visible.add('/photos-partage');
  if (can('promotions.encoder') || can('promotions.valider')) visible.add('/promotions');
  if (can('regularisation.acceder')) visible.add('/regularisation');
  // Feuille de dépense : accordée par défaut aux livreurs — qui n'ont pas de
  // sidebar — et au cas par cas aux profils de bureau depuis la matrice.
  if (can('depenses.declarer')) visible.add('/ma-feuille-depense');
  // Validation : admin + secrétaire au seed, ajustable dans la matrice.
  if (can('depenses.valider')) visible.add('/validation-depenses');
  // Analyse : pilotage, admin au seed.
  if (can('depenses.analyser')) visible.add('/analyse-depenses');

  // ENTRÉES OUVERTES PAR LA PERMISSION (et non par la table de menus héritée).
  // La sidebar se construisait UNIQUEMENT à partir des menus enregistrés en base
  // pour l'utilisateur : quelqu'un à qui l'admin accorde un droit dans la matrice
  // le possédait réellement (l'API le laissait passer) mais ne voyait AUCUNE
  // entrée pour s'en servir. Cas concret : un LIVREUR à PROFIL MIXTE qui bascule
  // en vendeur n'a aucun menu en base → sidebar vide, fonctionnalité inutilisable.
  // On ajoute donc l'entrée dès que le droit correspondant est accordé.
  // La permission est AUTORITAIRE sur ces routes : on retire d'abord (un vieux
  // menu en base survivrait sinon à un refus explicite de l'admin), puis on
  // rajoute si le droit est réellement accordé. Même discipline que les modules
  // gouvernés ci-dessus.
  const parPermission = [
    ['commandes.voir', '/listeCommande'],
    ['commandes.creer', '/commandeForm'],
    ['ventes.voir', '/ventes'],
    ['clientes.voir', '/clients'],
    ['retours.voir', '/retour'],
    ['retours.creer', '/retourForm'],
    ['produits.voir', '/products'],
    ['creances.voir', '/creances'],
    ['creances.encaisser', '/paiement'],
    ['livraisons.voir', '/livraison_detail'],
    ['reservations.voir', '/reservations'],
  ];
  // Le retrait préalable ne s'applique qu'au LIVREUR (profil mixte) : ses menus
  // en base sont vides, donc c'est sans effet de bord, et un refus explicite de
  // l'admin masque bien l'entrée. On NE touche PAS aux autres profils : un vrai
  // vendeur peut avoir en base des entrées (livraisons, réservations) absentes
  // de son seed de rôle — les retirer lui ferait perdre un accès existant.
  if (userRole === 'livreur') parPermission.forEach(([, route]) => visible.delete(route));
  parPermission.forEach(([cle, route]) => { if (can(cle)) visible.add(route); });

  const has = (route) => visible.has(route);
  const itemVisible = (it) => (it.sub ? it.sub.some((s) => has(s.route)) : has(it.route));

  const [openKeys, setOpenKeys] = useState(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const sec = SECTIONS.find((s) => s.items.some((it) => (it.sub ? it.sub.some((x) => x.route === path) : it.route === path)));
    return sec ? [sec.key] : [];
  });

  const onOpenChange = (keys) => {
    const latest = keys.find((k) => openKeys.indexOf(k) === -1);
    if (latest && rootKeys.indexOf(latest) !== -1) {
      setOpenKeys([latest, ...keys.filter((k) => rootKeys.indexOf(k) === -1)]);
    } else {
      setOpenKeys(keys);
    }
  };

  const Logout = async () => {
    try {
      // Push natif : retire CE téléphone du fichier des destinataires AVANT de
      // perdre la session (best-effort — un token oublié sera réaffecté au
      // prochain login ou purgé par le serveur s'il meurt).
      const pushToken = localStorage.getItem('push_token');
      if (pushToken) {
        await axios.delete(`${DOMAIN}/api/notifications/push-token`, { data: { token: pushToken } }).catch(() => {});
        localStorage.removeItem('push_token');
      }
      await axios.post(`${DOMAIN}/api/auth/logout`);
      setCurrentUser(null);
      localStorage.setItem('persist:root', JSON.stringify(currentUser));
      Swal.fire('Déconnexion réussie !', '', 'success');
      navigate('/login');
      window.location.reload();
    } catch (error) {
      Swal.fire('Erreur lors de la déconnexion.', '', 'error');
    }
  };

  const go = (route) => { dispatch(toggleSidebar()); };

  const renderItem = (it) => {
    if (it.sub) {
      const subs = it.sub.filter((s) => has(s.route));
      if (!subs.length) return null;
      // UN GROUPE À UN SEUL ÉLÉMENT N'EST PAS UN GROUPE.
      //
      // Les sous-menus sont filtrés par permission : « Commandes » en contient
      // deux au catalogue, mais « Commandes dormantes » disparaît pour qui n'y a
      // pas droit. Il restait alors un chevron à déplier pour atteindre un seul
      // lien — deux gestes au lieu d'un, et une hiérarchie qui ment sur ce
      // qu'elle contient.
      //
      // Le libellé retenu est celui du GROUPE (« Commandes ») et non celui de
      // l'entrée (« Liste des commandes ») : c'est le mot que l'œil cherche
      // dans un menu, et il reste stable quand un droit s'ajoute.
      if (subs.length === 1) {
        return (
          <Item key={`${it.label}|${subs[0].route}`}>
            <Link to={subs[0].route} className="sidebarLink" onClick={go}>{it.label}</Link>
          </Item>
        );
      }
      return (
        <SubMenu key={`grp-${it.label}`} title={<span className="sidebarLink">{it.label}</span>}>
          {subs.map((s) => (
            /* Clé préfixée par le groupe : une même route peut vivre dans DEUX
               groupes (ex. « Ventes manquées & zones » en Clientes ET Stock) —
               sans préfixe, antd recevrait deux items de même clé. */
            <Item key={`${it.label}|${s.route}`}>
              <Link to={s.route} className="sidebarLink" onClick={go}>{s.label}</Link>
            </Item>
          ))}
        </SubMenu>
      );
    }
    if (!has(it.route)) return null;
    return (
      <Item key={it.route}>
        <Link to={it.route} className="sidebarLink" onClick={go}>{it.label}</Link>
      </Item>
    );
  };

  return (
    <>
    {/* Backdrop mobile : un tap hors du panneau le referme (comportement
        standard des tiroirs Android) — inerte sur desktop (display:none). */}
    {isSidebarOpen && (
      <div className="sidebar-backdrop" onClick={() => dispatch(toggleSidebar())} aria-hidden="true" />
    )}
    <Menu
      mode="inline"
      theme="dark"
      ref={sidebarRef}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      className={`sidebar ${isSidebarOpen ? 'visible' : ''}`}
    >
      {has('/listeCommande') && (
        <Item key="/commandeForm" className="newCommandeItem" icon={<PlusOutlined style={{ fontSize: '18px', color: '#071C3D' }} />} onClick={go}>
          <Link to="/commandeForm" style={{ fontSize: '14px', fontWeight: 600, color: '#071C3D' }}>Nouvelle commande</Link>
        </Item>
      )}

      {SECTIONS.map((sec) => {
        const items = sec.items.filter(itemVisible);
        if (!items.length) return null;
        const Icon = sec.icon;
        return (
          <SubMenu
            key={sec.key}
            icon={<Icon style={{ fontSize: '18px', color: '#e6ddcd' }} />}
            title={<span className="sidebarH3">{sec.title}</span>}
          >
            {items.map(renderItem)}
          </SubMenu>
        );
      })}

      <Menu.Divider className="navFootDivider" />
      <Item key="apropos" icon={<CloudSyncOutlined style={{ fontSize: '18px', color: '#e6ddcd' }} />}
        onClick={() => { dispatch(toggleSidebar()); window.dispatchEvent(new Event('ndoe:apropos')); }}>
        <span className="sidebarH3" style={{ fontSize: '13.5px', color: '#f1ece3' }}>Version &amp; mise à jour</span>
      </Item>
      <Item key="/profile" icon={<UserOutlined style={{ fontSize: '18px', color: '#e6ddcd' }} />} onClick={go}>
        <Link to="/profile" className="sidebarH3" style={{ fontSize: '13.5px', color: '#f1ece3' }}>Mon profil</Link>
      </Item>
      <Item key="logout" className="navLogoutItem" icon={<LogoutOutlined style={{ fontSize: '18px', color: '#e6ddcd' }} />} onClick={Logout}>
        <span className="sidebarH3" style={{ color: '#f1ece3' }}>Déconnexion</span>
      </Item>
    </Menu>
    </>
  );
};

export default Options;
